import React, { useEffect, useState } from 'react';
import dataService from 'api/data.service';
import { CircularProgress, Card, Divider } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useTranslation } from 'react-i18next';
import { ReactComponent as NoData } from 'assets/svgs/noData.svg';
import VuiTypography from 'components/VuiTypography';
import VuiBox from "components/VuiBox";

const AnnouncementsList = () => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const data = await dataService.getAnnouncements();
        setAnnouncements(data.resources);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={32} sx={{ color: "#0075ff" }} />
      </VuiBox>
    );
  }

  if (error) {
    return <VuiTypography color="error">{t('error', { error })}</VuiTypography>;
  }

  if (announcements.length === 0) {
    return (
      <VuiBox mt={3} textAlign="center">
        <NoData style={{ width: '50%', height: 'auto', marginBottom: '20px' }} />
        <VuiTypography variant="h5" color="light">{t('noAnnouncements')}</VuiTypography>
      </VuiBox>
    );
  }

  return (
    <VuiBox mt={3}>
      <VuiBox display="flex" alignItems="center" mb={2} gap={1}>
        <CampaignIcon sx={{ color: "#0075ff", fontSize: "1.4rem" }} />
        <VuiTypography variant="h6" color="white" fontWeight="bold" sx={{ letterSpacing: "0.03em" }}>
          {t('announcements') || 'Announcements'}
        </VuiTypography>
      </VuiBox>

      {announcements.map((announcement, index) => (
        <Card
          key={announcement.id}
          sx={{
            mb: 2,
            background: "linear-gradient(126.97deg, rgba(6,11,40,0.92) 28.26%, rgba(10,14,35,0.85) 91.2%)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderLeft: "3px solid #0075ff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            transition: "transform 0.18s ease, box-shadow 0.18s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 8px 28px rgba(0,117,255,0.18)",
              borderLeft: "3px solid #4318ff",
            },
          }}
        >
          <VuiBox>
            <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <VuiTypography variant="h6" color="white" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                {announcement.title}
              </VuiTypography>
              <VuiTypography
                variant="caption"
                fontWeight="regular"
                sx={{ color: "#a0aec0", ml: 2, whiteSpace: "nowrap", mt: "2px" }}
              >
                {new Date(announcement.published_at).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric'
                })}
              </VuiTypography>
            </VuiBox>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", my: 1 }} />
            <VuiTypography
              variant="body2"
              fontWeight="regular"
              sx={{ color: "#a0aec0", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: announcement.content }}
            />
          </VuiBox>
        </Card>
      ))}
    </VuiBox>
  );
};

export default AnnouncementsList;
